import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ConfluenceOnContentModified } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import CollaboratorsQuery, {
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery as CollaboratorsQueryType,
} from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';
import { getContentCollaboratorsQueryVariables } from './getContentCollaboratorsQueryVariables.ts';
import { useContributorsRefresh } from './useContributorsRefresh.ts';
import { useContributorsActivityRefresh } from './useContributorsActivityRefresh.ts';
import { useLiveContributorsActivity } from './useLiveContributorsActivity.ts';
import { useEditorCollaboratorsFragment } from './useEditorCollaboratorsFragment.ts';

type CollaboratorsQueryResult = {
	data?: CollaboratorsQueryType['response'];
	loading: boolean;
	error?: Error;
	refetch: () => Promise<unknown>;
};

const CollaboratorsQueryContext = createContext<CollaboratorsQueryResult | undefined>(undefined);
export const useCollaboratorsQueryResult = (): CollaboratorsQueryResult | undefined =>
	useContext(CollaboratorsQueryContext);

// Error boundaries retain their fallback component; context keeps children and retry current.
const FallbackContext = createContext<{
	children: React.ReactNode;
	refetch: () => Promise<unknown>;
}>({ children: null, refetch: async () => undefined });

const QueryErrorFallback = ({ error }: { error: Error }) => {
	const { children, refetch } = useContext(FallbackContext);
	const value = useMemo(() => ({ loading: false, error, refetch }), [error, refetch]);
	return (
		<CollaboratorsQueryContext.Provider value={value}>
			{children}
		</CollaboratorsQueryContext.Provider>
	);
};

type CollaboratorsQueryProviderProps = {
	children: React.ReactNode;
	isDropdownOpen: boolean;
};

// Mounted only on the dual-agent byline path.
export const CollaboratorsQueryProvider = (
	props: CollaboratorsQueryProviderProps,
): React.JSX.Element => {
	const isLivePage = useIsCurrentPageLive();

	return <RefreshingCollaboratorsQueryProvider {...props} isLivePage={isLivePage} />;
};

// The preloader warms the same Relay operation; both consumers read its shared result synchronously.
const RefreshingCollaboratorsQueryProvider = ({
	children,
	isDropdownOpen,
	isLivePage,
}: CollaboratorsQueryProviderProps & {
	isLivePage: boolean;
}): React.JSX.Element => {
	const environment = useRelayEnvironment();
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const variables = useMemo(
		() =>
			getContentCollaboratorsQueryVariables({
				cloudId,
				contentId: contentId || '',
				spaceKey: spaceKey || '',
			}),
		[cloudId, contentId, spaceKey],
	);
	const [retryCount, setRetryCount] = useState(0);
	const refetch = useCallback(
		() =>
			fetchQuery<CollaboratorsQueryType>(environment, CollaboratorsQuery, variables, {
				fetchPolicy: 'network-only',
			}).toPromise(),
		[environment, variables],
	);
	const retry = useCallback(async () => {
		await refetch();
		setRetryCount((count) => count + 1);
	}, [refetch]);
	const fallbackValue = useMemo(() => ({ children, refetch: retry }), [children, retry]);
	const loadingValue = useMemo(() => ({ loading: true, refetch: retry }), [retry]);
	const editorCollaboratorsFragment = useEditorCollaboratorsFragment({ contentId });

	if (!cloudId || !contentId || !spaceKey) {
		return <>{children}</>;
	}

	if (editorCollaboratorsFragment.isProvided) {
		return (
			<EditorCollaboratorsProvider
				collaborators={editorCollaboratorsFragment}
				variables={variables}
				refetch={refetch}
				isDropdownOpen={isDropdownOpen}
				isLivePage={isLivePage}
			>
				{children}
			</EditorCollaboratorsProvider>
		);
	}

	return (
		<FallbackContext.Provider value={fallbackValue}>
			<GenericErrorBoundary
				key={`${cloudId}:${contentId}:${spaceKey}:${retryCount}`}
				attribution={Attribution.PAGE_EXPERIENCES}
				renderOnError={QueryErrorFallback}
			>
				<RelaySuspense
					interactionName="collaborators-byline"
					fallback={
						<CollaboratorsQueryContext.Provider value={loadingValue}>
							{children}
						</CollaboratorsQueryContext.Provider>
					}
				>
					<QueriedCollaboratorsProvider
						variables={variables}
						refetch={refetch}
						fetchKey={retryCount}
						isDropdownOpen={isDropdownOpen}
						isLivePage={isLivePage}
					>
						{children}
					</QueriedCollaboratorsProvider>
				</RelaySuspense>
			</GenericErrorBoundary>
		</FallbackContext.Provider>
	);
};

type CollaboratorsRefreshOptions = {
	variables: CollaboratorsQueryType['variables'];
	refetch: CollaboratorsQueryResult['refetch'];
	isDropdownOpen: boolean;
	isLivePage: boolean;
};

const useCollaboratorsRefreshing = (
	{ variables, refetch, isDropdownOpen, isLivePage }: CollaboratorsRefreshOptions,
	{ refreshOnMount }: { refreshOnMount?: boolean } = {},
) => {
	const [contentType] = useContentType();
	const { error, refreshNow } = useContributorsRefresh(refetch, { refreshOnMount });
	const onActivity = useContributorsActivityRefresh({
		contentId: variables.id,
		enabled: isLivePage,
		refreshNow,
	});
	useLiveContributorsActivity({
		enabled: isLivePage,
		cloudId: variables.cloudId,
		contentId: variables.id,
		contentType,
		onActivity,
	});
	const onPageUpdated = useCallback(
		(_eventName: string, response: ConfluenceOnContentModified) => {
			if (response.id === variables.id && response.pageUpdated?.trigger === 'publish') {
				// Classic contributors change on publish, regardless of who published the page.
				void refreshNow({ queueIfInFlight: true });
			}
		},
		[variables.id, refreshNow],
	);
	useSubscriptionEvent({
		contentId: variables.id,
		contentType,
		delta: 'pageUpdated',
		subscriberId: 'bylineContributorsPageUpdated',
		onDataReceived: onPageUpdated,
		skip: isLivePage || (contentType !== 'page' && contentType !== 'blogpost'),
	});
	useEffect(() => {
		if (isDropdownOpen) {
			// Opening the popup refreshes both views.
			void refreshNow();
		}
	}, [isDropdownOpen, refreshNow]);

	return { error, refreshNow };
};

const QueriedCollaboratorsProvider = ({
	children,
	fetchKey,
	...refreshOptions
}: CollaboratorsRefreshOptions & {
	children: React.ReactNode;
	fetchKey: number;
}) => {
	const data = useLazyLoadQuery<CollaboratorsQueryType>(
		CollaboratorsQuery,
		refreshOptions.variables,
		{
			fetchPolicy: 'store-or-network',
			fetchKey,
		},
	);
	const { error, refreshNow } = useCollaboratorsRefreshing(refreshOptions);
	const value = useMemo(
		() => ({ data, loading: false, error, refetch: refreshNow }),
		[data, error, refreshNow],
	);

	return (
		<CollaboratorsQueryContext.Provider value={value}>
			{error && <ErrorDisplay error={error} />}
			{children}
		</CollaboratorsQueryContext.Provider>
	);
};

// The refreshes fetch the collaborators query, which updates the fields the fragment reads. The editor
// fetches its document on each entry, so mounting does not refresh it.
const EditorCollaboratorsProvider = ({
	children,
	collaborators,
	...refreshOptions
}: CollaboratorsRefreshOptions & {
	children: React.ReactNode;
	collaborators: EditorContentFragmentResult<CollaboratorsQueryType['response']>;
}) => {
	const { error: refreshError, refreshNow } = useCollaboratorsRefreshing(refreshOptions, {
		refreshOnMount: false,
	});
	const error = collaborators.error ?? refreshError;
	const value = useMemo(
		() => ({
			data: collaborators.data,
			loading: collaborators.loading,
			error,
			refetch: refreshNow,
		}),
		[collaborators.data, collaborators.loading, error, refreshNow],
	);

	return (
		<CollaboratorsQueryContext.Provider value={value}>
			{error && <ErrorDisplay error={error} />}
			{children}
		</CollaboratorsQueryContext.Provider>
	);
};
